import * as Sentry from "@sentry/vue";
import { VueQueryPlugin, type VueQueryPluginOptions } from "@tanstack/vue-query";
import VueDatePicker from "@vuepic/vue-datepicker";
import { QuillEditor } from "@vueup/vue-quill";
import DOMPurify from "dompurify";
import Keycloak from "keycloak-js";
import { createPinia } from "pinia";
import { createApp } from "vue";

import App from "@/App.vue";
import { BACKEND_URL } from "@/constants";
import { router } from "@/router";
import { useAuth } from "@/stores/auth";

import "@vuepic/vue-datepicker/dist/main.css";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import "intro.js/introjs.css";
import "./styles.css";

declare global {
  interface Window {
    trustedTypes?: {
      createPolicy: (
        name: string,
        rules: {
          createHTML: (string: string) => string;
          createScriptURL: (s: string, type: string, sink: string) => string;
        },
      ) => string;
    };
  }
}

if (window.trustedTypes && window.trustedTypes.createPolicy) {
  window.trustedTypes.createPolicy("default", {
    createHTML: (string) =>
      DOMPurify.sanitize(string, { RETURN_TRUSTED_TYPE: true }) as unknown as string,
    createScriptURL: (s, type, sink) => {
      return `${s}?default-policy-used&type=${encodeURIComponent(
        type,
      )}&sink=${encodeURIComponent(sink)}`;
    },
  });
}

// Configs
const vueQueryConfig: VueQueryPluginOptions = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        retry: 1, // Retry only once
        retryDelay: 2000, // Forced constant delay
      },
    },
  },
};

// App creation
const app = createApp(App);

const storedDsn = localStorage.getItem("sentryDsn");
if (storedDsn) {
  Sentry.init({
    app,
    dsn: storedDsn,
    sendDefaultPii: true,
    integrations: [],
  });
}

// Plugins
app.use(createPinia());
app.use(router);
app.use(VueQueryPlugin, vueQueryConfig);

// Components
app.component("QuillEditor", QuillEditor);
app.component("VueDatePicker", VueDatePicker);

// Mount
const auth = useAuth();
const kc = new Keycloak(`//${BACKEND_URL}/openid/keycloak.json`);
auth.setKeycloak(kc);
auth.initializeKeycloak(app);

fetch(`//${BACKEND_URL}/config`)
  .then((res) => res.json())
  .then((config) => {
    if (config.sentryDsn) {
      localStorage.setItem("sentryDsn", config.sentryDsn);
    }
  })
  .catch((err) => {
    console.warn("Failed to fetch config:", err);
  });
